/**
 * GET `admin/payments` — журнал платежей FreedomPay + сводка за период.
 * Бэк отдаёт страницами (по умолчанию 25) — забираем все страницы периода,
 * фильтры и поиск экрана работают по полному журналу. Тыйыны → сомы.
 */

import {apiEnabled, http} from '@services/utils';

import {MOCK_PAYMENTS, PAYMENTS_SUMMARY, PAYMENTS_TOTAL} from '../mocks';
import type {PaymentRow, PaymentsBundle, PaymentStatus} from '../types';

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

type Raw = Record<string, unknown>;
const asObj = (v: unknown): Raw => (typeof v === 'object' && v !== null ? (v as Raw) : {});
const asNum = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);
const asStr = (v: unknown): string => (typeof v === 'string' ? v : '');
const toSom = (tiyin: unknown): number => Math.round(asNum(tiyin) / 100);

const STATUSES: readonly PaymentStatus[] = ['success', 'pending', 'failed', 'refund'];

/** Страница журнала (максимум бэка) и потолок страниц — 2000 строк за период */
const PAGE_SIZE = 100;
const MAX_PAGES = 20;

const toRow = (v: unknown): PaymentRow => {
  const r = asObj(v);
  return {
    id: asStr(r.id),
    date: asStr(r.date),
    client: asStr(r.client),
    clientType: r.clientType === 'clinic' ? 'clinic' : 'solo',
    type: asStr(r.type),
    period: asStr(r.period),
    amount: toSom(r.amountTiyin),
    // Неизвестный статус — «в обработке», а не «успешно»: деньги не пришли, пока бэк не сказал
    status: STATUSES.includes(r.status as PaymentStatus) ? (r.status as PaymentStatus) : 'pending',
    method: asStr(r.method),
    txnId: asStr(r.txnId),
  };
};

/** `from`/`to` — даты периода (BE ждёт YYYY-MM-DD). */
export const getPayments = async (from?: string, to?: string): Promise<PaymentsBundle> => {
  if (!apiEnabled) {
    await sleep(240);
    return {
      summary: {...PAYMENTS_SUMMARY},
      rows: MOCK_PAYMENTS.map((r) => ({...r})),
      totalCount: PAYMENTS_TOTAL,
    };
  }

  const page = (n: number) =>
    http<Raw>('admin/payments', {
      params: {
        ...(from !== undefined && {from: from.slice(0, 10)}),
        ...(to !== undefined && {to: to.slice(0, 10)}),
        page: n,
        pageSize: PAGE_SIZE,
      },
      skipCaseConversion: true,
    });
  const raw = await page(1);
  const rows = Array.isArray(raw.rows) ? [...raw.rows] : [];
  const pages = Math.min(MAX_PAGES, Math.ceil(asNum(raw.totalCount) / PAGE_SIZE));
  for (let n = 2; n <= pages; n++) {
    const next = await page(n);
    if (Array.isArray(next.rows)) rows.push(...next.rows);
  }
  const s = asObj(raw.summary);
  return {
    summary: {
      periodLabel: asStr(s.periodLabel),
      successSum: toSom(s.successSumTiyin),
      successCount: asNum(s.successCount),
      failedCount: asNum(s.failedCount),
      failedSum: toSom(s.failedSumTiyin),
      refundSum: toSom(s.refundSumTiyin),
      refundCount: asNum(s.refundCount),
      net: toSom(s.netTiyin),
    },
    rows: rows.map(toRow),
    totalCount: asNum(raw.totalCount),
  };
};
